/* eslint-disable import/prefer-default-export */
/* eslint-disable arrow-body-style */

import { useEffect, useState, useMemo, useRef } from "react";
import { useHistory } from "hooks/useHistory";
import { Pill, CollectionItem } from "../types";
// import gtagEventEmitter from "helpers/gtagEventEmitter";
import customHistoryPush from "@Utils/customHistoryPush";
import { getWhiteLabelSearchRedirectionURL } from "@Pages/WhitelabelHome/Desktop/helpers/WhiteLabelRedirectionHelper";
// import { PAGE } from "constants/configConstants";
import { isEmptyObject } from "helpers/validations";
import getUserCountry from "@Pages/Search/components/SearchList/components/TrustPilotBanner/helper";
import countryCallingCodes from "constants/countryCallingCodes";

interface UseThousandPropertiesProps {
  collection: CollectionItem[];
  showSeeMoreCard: boolean;
  isDesktop: boolean;
  isShowPills: boolean;
  countryCityMapping: any;
  thousandProperties: any;
  gtagEventActionPill: string | null;
  fetchData: any;
  isWhitelabel: boolean;
  whitelabelConfig: any;
  whitelabel_region_canonical_name: string;
  isDemoSubdomain: boolean;
  partner_uuid: string;
  recentlyViewedCityCanonical: string;
}

export const useThousandProperties = ({
  collection,
  showSeeMoreCard,
  isDesktop,
  isShowPills,
  countryCityMapping,
  thousandProperties,
  gtagEventActionPill,
  fetchData,
  isWhitelabel,
  whitelabelConfig,
  whitelabel_region_canonical_name,
  isDemoSubdomain,
  partner_uuid,
  recentlyViewedCityCanonical,
}: UseThousandPropertiesProps) => {
  const history = useHistory();

  const mapIsoToCountryName = (iso?: string): string | null => {
    if (!iso) return null;
    const entry = countryCallingCodes[iso as keyof typeof countryCallingCodes];
    if (!entry?.name) return null;
    const countryKeys = Object.keys(countryCityMapping);
    return countryKeys.find((key) => entry.name === key || entry.name.includes(key)) || null;
  };

  const domesticCountryRef = useRef<string>("United Kingdom");

  const [activeCountry, setActiveCountry] = useState<string>("United Kingdom");
  const [pills, setPills] = useState<Pill[]>(countryCityMapping["United Kingdom"]?.city || []);
  const [isCountryMenuOpen, setIsCountryMenuOpen] = useState(false);
  const [activePill, setActivePill] = useState<string | null>(pills?.[0]?.name ?? null);
  const [activePillCanonical, setActivePillCanonical] = useState<string | null>(
    pills?.[0]?.canonical_name || pills?.[0]?.canonicalName || null,
  );
  const [isRequestActive, setIsRequestActive] = useState(false);

  // Initialize collection state
  const [collectionState, setCollectionState] = useState<CollectionItem[] | "exploreMore">(() => {
    if (!showSeeMoreCard || !collection) return collection;
    return collection.length < 10 ? [...collection, collection[0]] : collection;
  });

  // Get display collection based on desktop and showSeeMoreCard
  const displayCollection = useMemo(() => {
    if (isDesktop || !showSeeMoreCard) return collectionState;
    return Array.isArray(collectionState) ? collectionState.slice(0, 5) : collectionState;
  }, [JSON.stringify(collectionState), isDesktop, showSeeMoreCard]);

  // Handle explore more navigation
  const handleExploreMore = () => {
    try {
      if (isWhitelabel) {
        const redirectionURL = getWhiteLabelSearchRedirectionURL(
          whitelabelConfig,
          whitelabel_region_canonical_name,
          isDemoSubdomain,
          partner_uuid,
        );
        customHistoryPush(history, redirectionURL);
        return;
      }

      const recentlyVisited = JSON.parse(localStorage.getItem("recentlyVisited") ?? "{}");
      const searchPath = `/places/search/${
        activePillCanonical ?? recentlyVisited?.[0]?.canonical_name ?? recentlyViewedCityCanonical
      }`;

      customHistoryPush(history, searchPath);
    } catch (error) {
      console.error("Error in handleExploreMore:", error);
      // Fallback to default search path
      customHistoryPush(history, "/places/search");
    }
  };

  // Fetch city data handler
  const fetchCityUniData = async (pill: Pill, country = "") => {
    if (!pill) return;

    try {
      setIsRequestActive(true);
      setCollectionState(
        thousandProperties[country || activeCountry]?.filter(
          (item: any) => item?.city === pill?.name,
        )?.[0]?.inventories || [],
      );

      if (gtagEventActionPill) {
        // gtagEventEmitter({
        //   action: gtagEventActionPill,
        //   event_category: PAGE.HOME,
        //   label: pill.name,
        //   country: country || "",
        // });
      }

      setActivePill(pill.name);
      setActivePillCanonical(pill.canonical_name || pill.canonicalName || null);

      if (fetchData) {
        const response = await fetchData(pill);
        const results = response?.result ?? [];

        // Update collection state based on results
        if (results.length === 0) {
          setCollectionState("exploreMore");
        } else if (results.length < 10 && showSeeMoreCard) {
          setCollectionState([...results, results[0]]);
        } else {
          setCollectionState(results);
        }
      }
    } catch (error) {
      console.error("Error fetching city data:", error);
      setCollectionState("exploreMore");
    } finally {
      setIsRequestActive(false);
    }
  };

  // Get SSR display collection
  const getSSRDisplayCollection = () => {
    return (
      thousandProperties?.["United Kingdom"]?.filter((item: any) => item.city === "London")?.[0]
        ?.inventories || []
    );
  };
  const getHiddenSSRData = () => {
    return isEmptyObject(thousandProperties) ? {} : thousandProperties;
  };

  useEffect(() => {
    if (!isShowPills) {
      setActivePill("Recently Viewed");
    }
  }, [isShowPills]);

  useEffect(() => {
    if (typeof window === "undefined") return;
    (async () => {
      try {
        const lsCode = localStorage.getItem("country");
        const res = lsCode ? { country: lsCode } : await getUserCountry();
        const detectedCountry = mapIsoToCountryName(res?.country) || "United Kingdom";
        domesticCountryRef.current = detectedCountry;
        if (detectedCountry !== activeCountry && countryCityMapping[detectedCountry]) {
          setActiveCountry(detectedCountry);
          const cityList = countryCityMapping[detectedCountry].city;
          setPills(cityList);
          if (cityList?.[0]) {
            setActivePill(cityList[0].name);
            setActivePillCanonical(cityList[0].canonical_name || cityList[0].canonicalName || null);
            fetchCityUniData(cityList[0], detectedCountry);
          }
        } else if (pills?.[0]) {
          setActivePill(pills[0].name);
          setActivePillCanonical(pills[0].canonical_name || pills[0].canonicalName || null);
          fetchCityUniData(pills[0], activeCountry);
        }
      } catch {
        // Fallback already set to UK
      }
    })();
  }, []);

  useEffect(() => {
    let val;
    if (!showSeeMoreCard || !collection) val = collection;
    else val = collection.length < 10 ? [...collection, collection[0]] : collection;
    setCollectionState(val);
  }, [JSON.stringify(collection), showSeeMoreCard]);

  return {
    activeCountry,
    setActiveCountry,
    pills,
    setPills,
    isCountryMenuOpen,
    setIsCountryMenuOpen,
    activePill,
    activePillCanonical,
    isRequestActive,
    collectionState,
    displayCollection,
    handleExploreMore,
    fetchCityUniData,
    getSSRDisplayCollection,
    getHiddenSSRData,
    domesticCountry: domesticCountryRef.current,
  };
};
